import { useCallback } from 'react';

import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { WebView } from '@zenbusiness/application-commons-webview';
import Banner from '@zenbusiness/zui/Banner';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import PropTypes from 'prop-types';

import { COUNTER_PARTY_STATUS } from '#client/constants/payees';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import usePlaid from '#client/hooks/usePlaid';
import useRedirectToLinkedAccounts from '#client/hooks/useRedirectToLinkedAccounts';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { BannerWrapperStyled } from '#client/pages/SharedStyles';
import { RELINK_ACTION_LABEL } from '#client/utils/counterpartyStatus';
import { retrievedGraphQLError } from '#client/utils/errors';
import { relinkPlaidWithNativeApp } from '#client/utils/Plaid/getPlaidWebview';

export default function RelinkBanner({
  isArchived,
  isNotRelinkable = false,
  callback = () => {},
  counterPartyId
}) {
  const { isWebView } = WebView;
  const { unitAccountId } = useUnitAccountId();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { redirectToLinkedAccounts } = useRedirectToLinkedAccounts(businessEntityUuid);
  const { createErrorToast } = useToastContext();
  const captureException = useCaptureException();

  const onCounterPartyReceived = (counterparty) => {
    if (counterparty) {
      callback(COUNTER_PARTY_STATUS.ACTIVE);
    }
  };
  const { isUpdatingPlaidLinkToken, updatePlaidLinkToken, isHandling } = usePlaid({
    unitAccountId,
    businessEntityUuid,
    onCounterPartyReceived,
    isRelink: true,
    counterPartyId
  });

  const reLinkHandler = useCallback(async () => {
    if (redirectToLinkedAccounts({ useTopWindow: true })) {
      return;
    }

    if (isWebView) {
      try {
        const { pathname } = document.location;

        relinkPlaidWithNativeApp({
          pathName: pathname,
          unitAccountId,
          counterpartyId: counterPartyId
        });
      } catch (error) {
        captureException(error, {
          level: 'warning'
        });
      }
    } else {
      const { error } = await updatePlaidLinkToken();
      if (error) {
        createErrorToast({
          title: retrievedGraphQLError(error),
          error
        });
      }
    }
  }, [
    captureException,
    counterPartyId,
    createErrorToast,
    unitAccountId,
    updatePlaidLinkToken,
    isWebView,
    redirectToLinkedAccounts
  ]);

  const handleLinkAccount = () => reLinkHandler();

  const bannerCopy = isNotRelinkable
    ? {
        title: 'This account can no longer be relinked',
        subtitle: 'Please create a new connection to continue.',
        buttonLabel: RELINK_ACTION_LABEL.CREATE_NEW_CONNECTION
      }
    : {
        title: 'Relink your bank account',
        subtitle: 'Your bank account is no longer linked. Please relink to continue.',
        buttonLabel: RELINK_ACTION_LABEL.RELINK
      };

  return (
    isArchived && (
      <BannerWrapperStyled>
        <Banner
          slotProps={{
            primaryAction: {
              label: bannerCopy.buttonLabel,
              onClick: handleLinkAccount,
              disabled: isUpdatingPlaidLinkToken || isHandling,
              loading: isUpdatingPlaidLinkToken || isHandling
            }
          }}
          slots={{
            startIcon: (
              <SharedAsset
                alt=""
                src="/icon/micro/alert-fill.svg"
              />
            )
          }}
          subtitle={bannerCopy.subtitle}
          sx={(theme) => ({ my: '30px', backgroundColor: theme.palette.background.grey.light })}
          title={bannerCopy.title}
        />
      </BannerWrapperStyled>
    )
  );
}

RelinkBanner.propTypes = {
  counterPartyId: PropTypes.string,
  isArchived: PropTypes.bool,
  isNotRelinkable: PropTypes.bool,
  callback: PropTypes.func
};
